// @file Upgrades a OneDrive/SharePoint sharing link that was pasted or typed as a plain link
// attachment into the same credentialed preview the OneDrive picker produces. A connected user
// whose token can read the file is upgraded silently. Someone who is not connected keeps the plain
// link, and a callout under its preview offers to connect: Connect goes straight to Microsoft's
// sign-in, and a successful connection upgrades that draft's link. Nothing interrupts the paste
// itself. A token that cannot read the file keeps the plain link, and if the person connected from
// the callout, the callout turns into an explanation.
import { trackEvent } from '@@/bits/analytics'
import { ContentPickerSource } from '@@/bits/content_picker'
import { getCurrentUser } from '@@/bits/current_user'
import device from '@@/bits/device'
import { captureException } from '@@/bits/error_tracker'
import { isMicrosoftOneDriveUrl } from '@@/bits/microsoft_onedrive_url'
import { isRegistered } from '@@/bits/user_model'
import {
  microsoftOneDriveUploadTypeFor,
  useMicrosoftOneDrivePickerStore,
} from '@@/pinia/microsoft_onedrive_picker_store'
import { useMicrosoftOneDriveUploadsStore } from '@@/pinia/microsoft_onedrive_uploads_store'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceDraftsStore } from '@@/pinia/surface_drafts'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { useSurfacePostPropertiesStore } from '@@/pinia/surface_post_properties'
import { UserIntegrationsApi } from '@@/surface/api/user_integrations'
import type { Cid, DraftPost, MicrosoftOneDriveResolvedDriveItem } from '@@/types'
import { useMicrosoftOneDrive } from '@@/vuecomposables/microsoft_onedrive'
import type { JsonAPIResource } from '@padlet/arvo'
import type { MicrosoftOneDriveCalloutKind } from '@padlet/universal-post-editor'
import { uniqueId } from 'es-toolkit/compat'
import { reactive } from 'vue'

function canUpgradeMicrosoftOneDriveLinks(): boolean {
  return (
    !device.app &&
    isRegistered(getCurrentUser()) &&
    useSurfacePermissionsStore().amIRegistered &&
    useSurfacePostPropertiesStore().isContentPickerSourceEnabled(ContentPickerSource.MicrosoftOneDrive)
  )
}

/**
 * Call right after `url` has been written onto the active draft as a plain link attachment.
 * Resolves asynchronously; the draft is only touched if it still carries that same link.
 */
function offerMicrosoftOneDriveUpgradeForActiveDraft(url: string): void {
  if (!isMicrosoftOneDriveUrl(url)) return
  if (!canUpgradeMicrosoftOneDriveLinks()) return

  const surfaceDraftsStore = useSurfaceDraftsStore()
  const draftCid = surfaceDraftsStore.activeDraftCid
  if (draftCid == null) return

  void upgradeDraftLink({ draftCid, url })
}

// The post editor hides a draft's generic link preview and shows its loading state while the draft
// carries a cloud picker source and an upload progress below 1 (see shouldKeepCloudPickerLoading).
// Marking the draft that way before the resolve request starts keeps the anonymous link preview,
// which for a private OneDrive/SharePoint link is a screenshot of Microsoft's sign-in page, from
// flashing up for the seconds it takes Graph to resolve and the upload to begin. The real upload
// takes over the progress in onStart; every other exit clears the mark so the plain link shows.
const PENDING_UPGRADE_PROGRESS = 0.01
const pendingUpgradeUrlByDraftCid = new Map<Cid, string>()

function markUpgradePending(draftCid: Cid, url: string): void {
  pendingUpgradeUrlByDraftCid.set(draftCid, url)
  useSurfaceDraftsStore().updateDraft({
    cid: draftCid,
    cloudPickerSource: 'microsoft_one_drive_picker',
    uploadProgress: PENDING_UPGRADE_PROGRESS,
  })
}

// Clears the pending mark unless a newer upgrade for the same draft owns it, or the user has since
// started a real upload (attachment changed and progress is no longer the pending sentinel).
function clearPendingUpgrade(draftCid: Cid, url: string): void {
  if (pendingUpgradeUrlByDraftCid.get(draftCid) !== url) return
  pendingUpgradeUrlByDraftCid.delete(draftCid)
  const draft = useSurfaceDraftsStore().draftByCid[draftCid]
  if (draft == null) return
  if (draft.attachment !== url && draft.uploadProgress !== PENDING_UPGRADE_PROGRESS) return
  useSurfaceDraftsStore().updateDraft({ cid: draftCid, cloudPickerSource: null, uploadProgress: undefined })
}

// The authorization helper leaves its promise pending when the OAuth window is closed.
// onCloseBeforeAuth is the only signal that the user gave up.
function connectOneDriveAccount(): Promise<void> {
  return new Promise((resolve, reject) => {
    let settled = false
    const finish = (error?: unknown): void => {
      if (settled) return
      settled = true
      if (error == null) resolve()
      else reject(error)
    }
    void useMicrosoftOneDrive()
      .requestMicrosoftOneDriveAuthorization({
        onCloseBeforeAuth: () => finish(new Error('OneDrive authorization window closed')),
      })
      .then(() => finish(), finish)
  })
}

async function isConnectedToOneDrive(): Promise<boolean> {
  const pickerStore = useMicrosoftOneDrivePickerStore()
  if (!pickerStore.isUserLinkedWithMicrosoftOneDrive) {
    await pickerStore.fetchMicrosoftOneDriveInfo()
  }
  return pickerStore.isUserLinkedWithMicrosoftOneDrive
}

// The callout each draft's preview carries, for the link it was shown for. Reactive so the composer
// shows, changes and hides the callout as it goes.
const calloutByDraftCid = reactive(new Map<Cid, { url: string; kind: MicrosoftOneDriveCalloutKind }>())
// Drafts whose Connect is waiting on Microsoft's sign-in window, so a second click opens no second window.
const connectingDraftCids = new Set<Cid>()

/**
 * The callout under the draft's preview, if any: only while the draft still carries the link it was
 * shown for. A Connect offer retires once OneDrive is connected, from any draft or from the
 * attachment picker.
 */
function microsoftOneDriveCalloutFor(
  draft: Pick<DraftPost, 'cid' | 'attachment'> | null | undefined,
): MicrosoftOneDriveCalloutKind | null {
  if (draft?.cid == null) return null
  const callout = calloutByDraftCid.get(draft.cid)
  if (callout == null || callout.url !== draft.attachment) return null
  if (callout.kind === 'connect' && useMicrosoftOneDrivePickerStore().isUserLinkedWithMicrosoftOneDrive) return null
  return callout.kind
}

/**
 * Connect on a draft's callout was clicked: open Microsoft's sign-in and, once connected, upgrade
 * the draft's link. The callout already says what connecting does, so no dialog comes first.
 * Closing the sign-in window keeps the callout, so the person can come back to it.
 */
async function connectMicrosoftOneDriveForDraft(draftCid: Cid): Promise<void> {
  const callout = calloutByDraftCid.get(draftCid)
  if (callout?.kind !== 'connect' || connectingDraftCids.has(draftCid)) return
  const { url } = callout
  trackEvent('Microsoft OneDrive', 'Paste connect callout clicked')

  connectingDraftCids.add(draftCid)
  try {
    await connectOneDriveAccount()
    await useMicrosoftOneDrivePickerStore().fetchMicrosoftOneDriveInfo()
  } catch {
    trackEvent('Microsoft OneDrive', 'Paste connect callout connection cancelled')
    return
  } finally {
    connectingDraftCids.delete(draftCid)
  }
  trackEvent('Microsoft OneDrive', 'Paste connect callout connection completed')
  calloutByDraftCid.delete(draftCid)

  const draftStillHasLink = (): boolean => useSurfaceDraftsStore().draftByCid[draftCid]?.attachment === url
  if (!draftStillHasLink()) return
  markUpgradePending(draftCid, url)
  const outcome = await resolveAndUploadDraftLink({ draftCid, url })

  // They connected on purpose, so a link the new connection still cannot read gets an explanation
  // rather than silently staying a plain link. Typical cause: a file someone else shared with them,
  // which the OneDrive permission Padlet asks for does not cover.
  if (outcome === 'unreadable' && draftStillHasLink()) {
    trackEvent('Microsoft OneDrive', 'Paste connect could not read file')
    calloutByDraftCid.set(draftCid, { url, kind: 'unreadable' })
  }
}

async function upgradeDraftLink({ draftCid, url }: { draftCid: Cid; url: string }): Promise<void> {
  markUpgradePending(draftCid, url)

  const surfaceDraftsStore = useSurfaceDraftsStore()
  const draftStillHasLink = (): boolean => surfaceDraftsStore.draftByCid[draftCid]?.attachment === url
  const connected = await isConnectedToOneDrive()
  if (!draftStillHasLink()) {
    clearPendingUpgrade(draftCid, url)
    return
  }
  if (!connected) {
    // Release the loading hold so the plain link's preview shows, with the Connect callout under it.
    clearPendingUpgrade(draftCid, url)
    calloutByDraftCid.set(draftCid, { url, kind: 'connect' })
    trackEvent('Microsoft OneDrive', 'Paste connect callout shown')
    return
  }

  await resolveAndUploadDraftLink({ draftCid, url })
}

// 'unreadable': the connection could not resolve the link (Graph refused it or found no item).
// 'link-gone': the draft no longer carries the link. 'upload-started': the copy is under way.
type ResolveOutcome = 'unreadable' | 'link-gone' | 'upload-started'

// Expects the draft to be marked pending for `url` (markUpgradePending); every exit clears it.
async function resolveAndUploadDraftLink({ draftCid, url }: { draftCid: Cid; url: string }): Promise<ResolveOutcome> {
  const surfaceDraftsStore = useSurfaceDraftsStore()
  const draftStillHasLink = (): boolean => surfaceDraftsStore.draftByCid[draftCid]?.attachment === url

  let driveItem: MicrosoftOneDriveResolvedDriveItem | undefined
  try {
    const response = await UserIntegrationsApi.resolveMicrosoftOneDriveSharingUrl(getCurrentUser().id, url)
    driveItem = (response.data as JsonAPIResource<MicrosoftOneDriveResolvedDriveItem> | undefined)?.attributes
  } catch {
    // The connection cannot read this link: the plain link stays as it is.
    clearPendingUpgrade(draftCid, url)
    trackEvent('Microsoft OneDrive', 'Pasted link kept as plain link')
    return 'unreadable'
  }
  if (driveItem == null) {
    clearPendingUpgrade(draftCid, url)
    return 'unreadable'
  }

  const surfaceStore = useSurfaceStore()

  // The user may have moved on while Graph was resolving; never clobber a newer attachment.
  if (!draftStillHasLink()) {
    clearPendingUpgrade(draftCid, url)
    return 'link-gone'
  }

  const fileType = driveItem.oneNote ? 'one' : (driveItem.name.split('.').pop()?.toLowerCase() ?? '')
  const uploadType = microsoftOneDriveUploadTypeFor({ fileSize: driveItem.size, fileType })
  trackEvent('Microsoft OneDrive', 'Pasted link upgrade started', fileType, driveItem.size)

  let attachedUrl = url
  const attachLink = (link: string, patch: Pick<DraftPost, 'cloudPickerSource' | 'uploadProgress'>): void => {
    attachedUrl = link
    surfaceDraftsStore.updateDraft({
      cid: draftCid,
      attachment: link,
      wish_content: { attachment_props: { url: link }, is_processed: false },
      ...patch,
    })
  }
  const draftStillHasUpgrade = (): boolean => surfaceDraftsStore.draftByCid[draftCid]?.attachment === attachedUrl

  useMicrosoftOneDriveUploadsStore().startUploadJob({
    uploadKey: uniqueId('ms-onedrive-paste-upgrade-'),
    fileId: driveItem.fileId,
    fileName: driveItem.name,
    fileType,
    fileSize: driveItem.size,
    driveId: driveItem.driveId,
    endpoint: driveItem.endpoint,
    accessToken: driveItem.accessToken,
    uploadType,
    webUrl: driveItem.webUrl,
    // The upload owner must be exactly one of wall/library/tenant: UploadsController#start answers
    // 400 "Specify only one of wallId, libraryId, tenantId" when several are named, which silently
    // drops the upgrade on any board that belongs to a tenant. On a board the wall is the owner,
    // which is also what the OneDrive picker sends.
    wallId: surfaceStore.wallId,
    onStart: (expectedUrl) => {
      if (!draftStillHasLink()) {
        clearPendingUpgrade(draftCid, url)
        return
      }
      // The real upload owns the progress from here on. 0.05 is the uploads store's first reported
      // value, so the bar moves off the pending sentinel the moment Beethoven accepts the job.
      if (pendingUpgradeUrlByDraftCid.get(draftCid) === url) pendingUpgradeUrlByDraftCid.delete(draftCid)
      attachLink(expectedUrl, { cloudPickerSource: 'microsoft_one_drive_picker', uploadProgress: 0.05 })
    },
    onProgress: (progress) => {
      if (!draftStillHasUpgrade()) return
      surfaceDraftsStore.updateDraft({ cid: draftCid, uploadProgress: progress })
    },
    onSuccess: (response) => {
      if (!draftStillHasUpgrade()) return
      attachLink(response.url, { cloudPickerSource: 'microsoft_one_drive_picker', uploadProgress: undefined })
      trackEvent('Microsoft OneDrive', 'Pasted link upgrade succeeded', fileType, driveItem.size)
    },
    onError: (response) => {
      captureException(new Error('Microsoft OneDrive pasted link upgrade failed'), {
        context: { errors: response.errors },
      })
      // The job can fail before onStart (for example the upload-attributes request). If the user
      // pasted another link meanwhile, the pending mark would otherwise outlive this upgrade and
      // keep the editor in its loading state; after onStart this is a no-op (the entry is gone).
      clearPendingUpgrade(draftCid, url)
      if (!draftStillHasUpgrade()) return
      // Beethoven could not ingest the file: put the plain link back exactly as pasted.
      attachLink(url, { cloudPickerSource: null, uploadProgress: undefined })
      trackEvent('Microsoft OneDrive', 'Pasted link upgrade failed', fileType, driveItem.size)
    },
  })
  return 'upload-started'
}

export { connectMicrosoftOneDriveForDraft, microsoftOneDriveCalloutFor, offerMicrosoftOneDriveUpgradeForActiveDraft }
